/**
 * useMaturityCredits — load the Maturity Credit factors once per session
 * (`GET /v2/credit-factors/claims-made`). Same shape as `useSirCredits`:
 * a module-level singleton cache + a mirrored state machine.
 *
 * Feeds the Schedule "Maturity Credit" dropdown.
 */
import { useEffect, useState } from "react";
import { getMaturityCredits } from "../api";
import type { MaturityCreditFactor } from "../api/endpoints/maturity-credits";

export type MaturityCreditsQueryState =
    | { status: "loading" }
    | { status: "error"; error: Error }
    | { status: "ready"; data: MaturityCreditFactor[] };

let inflight: Promise<MaturityCreditFactor[]> | null = null;

function loadOnce(): Promise<MaturityCreditFactor[]> {
    if (inflight) return inflight;
    inflight = getMaturityCredits().catch((error) => {
        inflight = null;
        throw error;
    });
    return inflight;
}

export function useMaturityCredits(): MaturityCreditsQueryState {
    const [state, setState] = useState<MaturityCreditsQueryState>({ status: "loading" });

    useEffect(() => {
        let cancelled = false;
        loadOnce()
            .then((data) => {
                if (cancelled) return;
                setState({ status: "ready", data });
            })
            .catch((error: unknown) => {
                if (cancelled) return;
                setState({
                    status: "error",
                    error: error instanceof Error ? error : new Error("Failed to load maturity credits"),
                });
            });
        return () => {
            cancelled = true;
        };
    }, []);

    return state;
}
